import { isCancel } from 'axios';

export const REQUEST_LIFECYCLE = {
  UNINITIALIZED: 'uninitialized',
  FETCHING: 'fetching',
  CANCELED: 'canceled',
  FULFILLED: 'fulfilled',
  ERROR: 'error',
};

/**
 * This Higher Order Function implements HTTP request lifecycle.
 * It injects performFulfillment and performSideEffect effects into actions payload.
 *   1. performFulfillment function receives data you want to populate
 *      into state along with REQUEST_LIFECYCLE.FULFILLED status.
 *   2. performSideEffect function receives function callback to invoke in the end of lifecycle.
 *      It is asynchronous.
 *
 * NOTE: It is not mutating return value of wrapped function.
 *
 * @example
 *  import { normalize } from 'normalizr';
 *
 *  fetchJob: flowRight(
 *     withRequestLifecycle('patch'), // 'patch' is local mutation
 *     () => async (context, { injected: { performFulfillment, performSideEffect }, jobId }) => {
 *       const { data } = await getJob(jobId, cancelToken);
 *
 *       performSideEffect(() => dispatch('action', { jobId })); // Any action you want to trigger as a response to current one
 *
 *       return performFulfillment(normalize(data, JOBS_SCHEMA));
 *     }
 *   )(),
 *
 * @param {string} dataViewMutationKey - key to determine which local mutation it can use to commit lifecycle data.
 * @param {string} [entitiesActionKey="entities/patch"] - key to determine which local mutation it can use to commit lifecycle data.
 *
 * @return {Function} - returns function which receives Vuex action.
 */
export const withRequestLifecycle = (
  dataViewMutationKey,
  entitiesActionKey = 'entities/patch'
) => fn => async (context, { injected, ...payload } = {}) => {
  const { commit, dispatch } = context;
  let fulfillmentData;
  let sideEffect;
  const performFulfillment = (data) => {
    fulfillmentData = data;

    return data;
  };
  const performSideEffect = (callback) => {
    sideEffect = callback;
  };

  try {
    commit(dataViewMutationKey, {
      meta: payload,
      status: REQUEST_LIFECYCLE.FETCHING,
      error: null,
    });

    const data = await fn(context, {
      ...payload,
      injected: { ...injected, performFulfillment, performSideEffect },
    });

    const { entities, ...dataView } = fulfillmentData || {};

    if (entities) {
      dispatch(entitiesActionKey, entities, { root: true });
    }

    commit(dataViewMutationKey, {
      meta: payload,
      status: REQUEST_LIFECYCLE.FULFILLED,
      ...dataView,
    });

    if (sideEffect) {
      setTimeout(sideEffect);
    }

    return data;
  } catch (error) {
    commit(dataViewMutationKey, {
      meta: payload,
      status: isCancel(error) ? REQUEST_LIFECYCLE.CANCELED : REQUEST_LIFECYCLE.ERROR,
      error,
    });

    throw error;
  }
};
